import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Density } from '../types';

interface ThemeContextValue {
  dark: boolean;
  setDark: (dark: boolean | ((prev: boolean) => boolean)) => void;
  density: Density;
  setDensity: (density: Density) => void;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean | ((prev: boolean) => boolean)) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [dark, setDarkState] = useState<boolean>(() => {
    try { return localStorage.getItem('sh24.dark') === '1'; } catch { return false; }
  });

  const [density, setDensityState] = useState<Density>(() => {
    try {
      const v = localStorage.getItem('sh24.density');
      return (v === 'compact' ? 'compact' : 'comfortable') as Density;
    } catch { return 'comfortable'; }
  });

  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    try { return localStorage.getItem('sh24.sidebar') === 'collapsed'; } catch { return false; }
  });

  useEffect(() => {
    document.body.classList.toggle('dark', dark);
    try { localStorage.setItem('sh24.dark', dark ? '1' : '0'); } catch {}
  }, [dark]);

  useEffect(() => {
    try { localStorage.setItem('sh24.density', density); } catch {}
  }, [density]);

  useEffect(() => {
    try { localStorage.setItem('sh24.sidebar', collapsed ? 'collapsed' : 'expanded'); } catch {}
  }, [collapsed]);

  const setDark = (value: boolean | ((prev: boolean) => boolean)) => {
    setDarkState(value);
  };

  const setDensity = (value: Density) => {
    setDensityState(value);
  };

  const setCollapsed = (value: boolean | ((prev: boolean) => boolean)) => {
    setCollapsedState(value);
  };

  return (
    <ThemeContext.Provider value={{ dark, setDark, density, setDensity, collapsed, setCollapsed }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
